import { useCallback, useEffect, useMemo, useState } from 'react';
import '../styles/dashboard-region-status.css';
import DashboardPageFrame from '../components/DashboardPageFrame.jsx';
import DrilldownModal from '../components/DrilldownModal.jsx';
import ScheduleRecordEditor from '../components/ScheduleRecordEditor.jsx';
import { getProgressSchedules } from '../api/dashboardApi';
import { DASHBOARD_TABS } from '../constants/tabs';
import {
  progressLabel,
  rollupProgress,
  hasInProgress,
  latestDate,
} from '../utils/regionStatusConverters.js';

const LARGE_CATEGORY_ORDER = [
  'プリセールス',
  'KDDI要件定義',
  '契約',
  'SIM',
  'サービスイン判定',
  '設計',
  '開発',
  '試験',
  'ラボリリース',
  '車載器試験',
  'NW ready',
  '商用リリース',
  'FS',
  'CV',
  '1A',
  '設備引継ぎ',
  '運用引継ぎ条件書',
  '運用サービスイン判定',
  'サービス運用引継ぎ',
  'プレ運用/運用トライアル',
  'LO',
  '運用開始',
  'EDER',
  '初回ADF',
];

function DataChip({ text, tone, isProgress, onClick }) {
  const className = [
    'dashboard-chip',
    `dashboard-chip--${tone}`,
    isProgress ? 'dashboard-chip--progress-border' : '',
    onClick ? 'dashboard-chip--clickable' : '',
  ].filter(Boolean).join(' ');
  return <span className={className} onClick={onClick}>{text}</span>;
}

// ================================================================
// メインコンポーネント
// ================================================================

export default function DashboardRegionStatusPage() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ドリルダウン state
  const [drilldownL1, setDrilldownL1] = useState(null);
  const [drilldownL2, setDrilldownL2] = useState(null);
  const [editingRow, setEditingRow] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getProgressSchedules();
      setSchedules(data);
    } catch (err) {
      console.error('[DashboardRegionStatusPage] loadData error:', err);
      setError('進捗状況データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    loadData().then(() => {
      /* istanbul ignore next -- アンマウント競合を防ぐ防御ガード（jsdom では発火しない） */
      if (!active) return;
    });
    return () => { active = false; };
  }, [loadData]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const tableData = useMemo(() => {
    const largeCatsInData = new Set(
      schedules.map((item) => item.large_category_name).filter(Boolean),
    );
    const dynamicColumns = LARGE_CATEGORY_ORDER.filter((cat) => largeCatsInData.has(cat));

    const oemRegionNames = Array.from(
      new Set(schedules.map((item) => item.oem_region).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b, 'ja'));

    const rows = oemRegionNames.map((oemRegion) => {
      const values = dynamicColumns.map((largeCat) => {
        const matches = schedules.filter(
          (item) =>
            item.oem_region === oemRegion && item.large_category_name === largeCat,
        );

        if (matches.length === 0) return { text: '-', tone: 'unknown', isProgress: false, matches: [], oemRegion, largeCat };

        const dateText = latestDate(matches) || '-';
        const labels = matches.map((m) => progressLabel(m.status_name, m.end_date_plan, today));
        const tone = rollupProgress(labels);
        const isProgress = hasInProgress(matches, today);

        return { text: dateText, tone, isProgress, matches, oemRegion, largeCat };
      });

      return { label: oemRegion, values };
    });

    return { columns: ['OEM×対地', ...dynamicColumns], rows };
  }, [schedules, today]);

  const gridTemplateColumns = useMemo(
    () => ['200px', ...tableData.columns.slice(1).map(() => '120px')].join(' '),
    [tableData.columns],
  );

  // ================================================================
  // ドリルダウンハンドラ
  // ================================================================

  const handleCellClick = useCallback((cellData) => {
    /* istanbul ignore next -- onClick は matches が存在する行のみ配線される防御ガード */
    if (!cellData.matches || cellData.matches.length === 0) return;
    setDrilldownL1({
      oemRegion: cellData.oemRegion,
      largeCat: cellData.largeCat,
      matches: cellData.matches,
    });
  }, []);

  const handleMiddleClick = useCallback((middleCat, items) => {
    setDrilldownL2({
      oemRegion: drilldownL1.oemRegion,
      largeCat: drilldownL1.largeCat,
      middleCat,
      items,
    });
  }, [drilldownL1]);

  const handleItemClick = useCallback((item) => {
    setEditingRow({ ...item });
  }, []);

  const handleSaved = useCallback(() => {
    setEditingRow(null);
    setDrilldownL1(null);
    setDrilldownL2(null);
    loadData();
  }, [loadData]);

  // ================================================================
  // Level 1: 中項目一覧
  // ================================================================

  const middleCategoryRows = useMemo(() => {
    if (!drilldownL1) return [];
    const groups = new Map();
    drilldownL1.matches.forEach((item) => {
      const key = item.middle_category_name || '（未分類）';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });
    return Array.from(groups.entries()).map(([name, items]) => {
      const labels = items.map((m) => progressLabel(m.status_name, m.end_date_plan, today));
      const tone = rollupProgress(labels);
      const date = latestDate(items) || '-';
      const alert = hasInProgress(items, today);
      return { name, items, tone, date, alert };
    });
  }, [drilldownL1, today]);

  // ================================================================
  // Level 2: 小項目一覧
  // ================================================================

  const smallCategoryRows = useMemo(() => {
    if (!drilldownL2) return [];
    return drilldownL2.items.map((item) => {
      const tone = progressLabel(item.status_name, item.end_date_plan, today);
      const date = item.end_date_plan || '-';
      const alert = item.status_name === '進行中' && item.end_date_plan && item.end_date_plan >= today;
      let name = item.small_category || item.middle_category_name || '（未分類）';
      if (item.phase_name) name += `【${item.phase_name}】`;
      return { name, tone, date, alert, item };
    });
  }, [drilldownL2, today]);

  // ================================================================
  // レンダー
  // ================================================================

  return (
    <DashboardPageFrame mainClassName="dashboard-main" rootClassName="dashboard-page" secondaryTabActiveIndex={0} secondaryTabs={DASHBOARD_TABS}>
        <section className="dashboard-panel" data-node-id="545:92">
          <div className="dashboard-panel-header" data-node-id="545:93" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 className="dashboard-panel-title" data-node-id="545:95">OEM×対地</h3>
            <div className="dashboard-legend">
              <span className="dashboard-legend-item"><span className="dashboard-chip dashboard-chip--plan dashboard-legend-swatch">予定</span>期限内</span>
              <span className="dashboard-legend-item"><span className="dashboard-chip dashboard-chip--delay dashboard-legend-swatch">遅延</span>期限超過</span>
              <span className="dashboard-legend-item"><span className="dashboard-chip dashboard-chip--completed dashboard-legend-swatch">完了</span>完了済み</span>
              <span className="dashboard-legend-item"><span className="dashboard-chip dashboard-chip--unknown dashboard-legend-swatch">-</span>未設定</span>
              <span className="dashboard-legend-item"><span className="dashboard-chip dashboard-chip--plan dashboard-chip--progress-border dashboard-legend-swatch">日付</span>進行中</span>
            </div>
          </div>
          <div className="dashboard-table" data-node-id="545:97">
            <div
              className="dashboard-table-row dashboard-table-row--head"
              data-node-id="545:99"
              style={{ gridTemplateColumns }}
            >
              {tableData.columns.map((column, index) => (
                <div
                  className={`dashboard-table-cell ${index === 0 ? 'is-sticky' : ''}`}
                  key={`${column}-${index}`}
                >
                  {column}
                </div>
              ))}
            </div>
            {loading && (
              <div className="dashboard-table-row" style={{ gridTemplateColumns }}>
                <div className="dashboard-table-cell" style={{ gridColumn: '1 / -1' }}>読み込み中...</div>
              </div>
            )}
            {!loading && error && (
              <div className="dashboard-table-row" style={{ gridTemplateColumns }}>
                <div className="dashboard-table-cell" style={{ gridColumn: '1 / -1' }}>{error}</div>
              </div>
            )}
            {!loading && !error && tableData.rows.length === 0 && (
              <div className="dashboard-table-row" style={{ gridTemplateColumns }}>
                <div className="dashboard-table-cell" style={{ gridColumn: '1 / -1' }}>データがありません</div>
              </div>
            )}
            {tableData.rows.map((row) => (
              <div className="dashboard-table-row" key={row.label} style={{ gridTemplateColumns }}>
                <div className="dashboard-table-cell dashboard-table-label">{row.label}</div>
                {row.values.map((value, index) => (
                  <div className="dashboard-table-cell" key={`${row.label}-${index}`}>
                    <DataChip
                      text={value.text}
                      tone={value.tone}
                      isProgress={value.isProgress}
                      onClick={value.matches && value.matches.length > 0 ? () => handleCellClick(value) : undefined}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>

      {/* Level 1: 中項目一覧モーダル */}
      {drilldownL1 && (
        <DrilldownModal
          title={`${drilldownL1.oemRegion} > ${drilldownL1.largeCat}`}
          onClose={() => setDrilldownL1(null)}
        >
          {/* istanbul ignore next -- モーダルは実データのあるセルからしか開かない */ middleCategoryRows.length === 0 ? (
            <div className="dashboard-drilldown-empty">中項目データがありません</div>
          ) : (
            middleCategoryRows.map((row) => (
              <div
                className={`dashboard-drilldown-row ${row.alert ? 'dashboard-drilldown-row--alert' : ''}`}
                key={row.name}
                onClick={() => handleMiddleClick(row.name, row.items)}
              >
                <span className="dashboard-drilldown-row-name">{row.name}</span>
                <div className="dashboard-drilldown-row-right">
                  <DataChip text={row.date} tone={row.tone} isProgress={row.alert} />
                </div>
              </div>
            ))
          )}
        </DrilldownModal>
      )}

      {/* Level 2: 小項目一覧モーダル */}
      {drilldownL2 && (
        <DrilldownModal
          title={`${drilldownL2.oemRegion} > ${drilldownL2.largeCat} > ${drilldownL2.middleCat}`}
          onClose={() => setDrilldownL2(null)}
        >
          {/* istanbul ignore next -- モーダルは実データのある中項目からしか開かない */ smallCategoryRows.length === 0 ? (
            <div className="dashboard-drilldown-empty">小項目データがありません</div>
          ) : (
            smallCategoryRows.map((row, idx) => (
              <div
                className={`dashboard-drilldown-row ${row.alert ? 'dashboard-drilldown-row--alert' : ''}`}
                key={`${row.name}-${idx}`}
                onClick={() => handleItemClick(row.item)}
              >
                <span className="dashboard-drilldown-row-name">{row.name}</span>
                <div className="dashboard-drilldown-row-right">
                  <DataChip text={row.date} tone={row.tone} isProgress={row.alert} />
                </div>
              </div>
            ))
          )}
        </DrilldownModal>
      )}

      {/* Level 3: 進捗管理レコード詳細 */}
      <ScheduleRecordEditor
        open={Boolean(editingRow)}
        initialRow={editingRow}
        onClose={() => setEditingRow(null)}
        onDeleted={handleSaved}
        onSaved={handleSaved}
      />
    </DashboardPageFrame>
  );
}
